/**
 * Team settings, as drafts: what the sheet holds while a person edits, and the
 * one save that writes only what changed.
 *
 * The sheet edits two kinds of thing. The Team (name, what it owns, its
 * repositories, what Bailey reads, whose tickets, where it posts, who it asks,
 * who sees it) goes through the Team's long-standing routes. Each Agent's
 * lines go through `PATCH /api/desks/:id/jobs/:jobId`, one call per line that
 * changed, carrying only the keys that changed. Nothing is written for a line
 * nobody touched, and a Save with nothing changed writes nothing at all.
 *
 * What Bailey reads is not a field of its own. "From a filter", "Only what I
 * hand it" and "Assigned to me" land on three existing things: the Team's
 * tracker attachment (the filter, kept as written), the propose line's switch
 * (Bailey picks, or never picks), and `desks.tickets_assigned_to` (one
 * person's tickets). The mode is read back from those three, never stored.
 */
import type {
  DeskAttachment,
  DeskAttachmentInput,
  Job,
  JobAuthority,
  JobCraft,
  JobTrigger,
  TeamAutonomy,
  TeamLadderReading,
} from "@yagni/shared";
import {
  DEFAULT_JOB_INSTANCE,
  isTeamLadderLineKind,
  ladderSettingsFor,
  readTeamLadder,
} from "@yagni/shared";

import { attachmentForFilterText, filterTextOf } from "../backlogFilterText.js";
import type { ReadsMode } from "../ReadsChoice.js";
import {
  answerersFrom,
  readsWrites,
  repoChanges,
  trackerAttachments,
  type AskRow,
  type SheetAudience,
} from "../teamEdit.js";
import {
  TeamWriteError,
  addTeamMember,
  attachBacklog,
  patchJob,
  removeTeamMember,
  saveAnswerers,
  saveTicketOwners,
  type PatchJobBody,
  type TeamAnswerers,
  type TeamMember,
} from "../teamsApi.js";
import { checkDraftChanged, saveCheckDrafts, type CheckDrafts } from "../ChecksRoster.js";
import { runLimitsCharter, type RunLimitsDraft } from "./runLimits.js";

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/** The sheet's anchors, in order: the Team, then one per Agent, then the Team's run limits. */
export const SETTINGS_SECTIONS = [
  "team",
  "bailey",
  "reeve",
  "wright",
  "proctor",
  "fletcher",
  "harper",
  "limits",
] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];
/** The sections that are an Agent's. */
export type AgentSettingsSection = Exclude<SettingsSection, "team" | "limits">;

/** Which section a craft's lines live under. Keyed by craft, so a renamed Agent still lands in its place. */
export const SECTION_OF_CRAFT: Record<JobCraft, SettingsSection> = {
  pm: "bailey",
  staff: "reeve",
  builder: "wright",
  reviewer: "proctor",
  qa: "fletcher",
  chronicler: "harper",
};

export const CRAFT_OF_SECTION: Record<AgentSettingsSection, JobCraft> = {
  bailey: "pm",
  reeve: "staff",
  wright: "builder",
  proctor: "reviewer",
  fletcher: "qa",
  harper: "chronicler",
};

/** `?settings=people`: the Team section, scrolled to its People. */
export const SETTINGS_PEOPLE_ANCHOR = "people";
/** `?settings=connections` opens the Team section scrolled to what it reads (YAG-986). */
export const SETTINGS_CONNECTIONS_ANCHOR = "connections";

export function isSettingsSection(value: string | null | undefined): value is SettingsSection {
  return value != null && (SETTINGS_SECTIONS as readonly string[]).includes(value);
}

/** The craft a kind belongs to: the part before the dot. */
export function craftOfKind(kind: string): JobCraft | null {
  const craft = kind.split(".")[0];
  return craft && craft in SECTION_OF_CRAFT ? (craft as JobCraft) : null;
}

/**
 * What `?settings=` names: a section, or (from the retired line pages) a line's
 * id, which opens the section of the Agent that holds it. Anything else opens
 * at the top.
 */
export function sectionFor(value: string | null, jobs: readonly Job[]): SettingsSection {
  if (isSettingsSection(value)) return value;
  const job = value ? jobs.find((entry) => entry.id === value) : undefined;
  const craft = job ? craftOfKind(job.kind) : null;
  return craft ? SECTION_OF_CRAFT[craft] : "team";
}

/** The line of a kind the sheet edits: the default instance first, then a live one, then any. */
export function lineFor(jobs: readonly Job[], kind: string): Job | null {
  const lines = jobs.filter((job) => job.kind === kind);
  return (
    lines.find((job) => job.instance === DEFAULT_JOB_INSTANCE) ??
    lines.find((job) => job.active) ??
    lines[0] ??
    null
  );
}

/**
 * The Team ladder step the Team reads as (decision 7, 2026-10-19): the step a
 * person picked, else the one its lines and its "Merge automatically" switch
 * describe, and whether a line was set apart from it (God Mode's override).
 */
export function teamLadderOfPage(
  team: { autonomy?: TeamAutonomy | null; autoMerge?: boolean } | null,
  jobs: readonly Job[],
): TeamLadderReading {
  return readTeamLadder(
    team?.autonomy ?? null,
    jobs.map((job) => ({ kind: job.kind, authority: job.authority, active: job.active })),
    team?.autoMerge === true,
  );
}

/**
 * The rung a ladder line runs at, as the sheet's drafts have it: the picked
 * step's, or the line's own while the Team reads "Custom (override)". Any
 * other line answers its own rung.
 */
export function ladderRungOf(
  autonomy: TeamAutonomy | null,
  job: Pick<Job, "kind">,
  draft: Pick<LineDraft, "authority">,
): JobAuthority {
  if (autonomy !== null && isTeamLadderLineKind(job.kind)) {
    return ladderSettingsFor(autonomy).authority;
  }
  return draft.authority ?? "supervised";
}

// ---------------------------------------------------------------------------
// A line's draft
// ---------------------------------------------------------------------------

export interface LineDraft {
  trigger: JobTrigger;
  filter: Record<string, unknown>;
  body?: Record<string, unknown>;
  note: string;
  authority: JobAuthority | null;
  supervisorUserId: string | null;
  active: boolean;
}

export function lineDraftOf(job: Job): LineDraft {
  return {
    trigger: structuredClone(job.trigger),
    filter: structuredClone((job.filter ?? {}) as Record<string, unknown>),
    ...(job.body ? { body: structuredClone(job.body) as unknown as Record<string, unknown> } : {}),
    note: job.note ?? "",
    authority: job.authority,
    supervisorUserId: job.supervisorUserId,
    active: job.active,
  };
}

/** Key-order-blind equality for the plain JSON a line carries. */
export function sameJson(a: unknown, b: unknown): boolean {
  return stable(a) === stable(b);
}

function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(",")}]`;
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([key, entry]) => `${JSON.stringify(key)}:${stable(entry)}`).join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

/** Keys the server owns on a filter: they are never sent back as edits. */
const SERVER_OWNED_FILTER_KEYS = ["approveOnGitHub", "harperJobId"];

function editableFilter(filter: Record<string, unknown>): Record<string, unknown> {
  const out = { ...filter };
  for (const key of SERVER_OWNED_FILTER_KEYS) delete out[key];
  return out;
}

/**
 * The PATCH a line needs, carrying only the keys that changed, or null when
 * nothing did. The filter goes whole (the server replaces it), the body field
 * by field (the server merges it, so an emptied field goes out as null).
 */
export function linePatch(job: Job, draft: LineDraft): PatchJobBody | null {
  const base = lineDraftOf(job);
  const patch: PatchJobBody = {};
  if (!sameJson(base.trigger, draft.trigger)) patch.trigger = draft.trigger;
  if (!sameJson(editableFilter(base.filter), editableFilter(draft.filter)))
    patch.filter = draft.filter;
  const body = draft.body ? bodyPatch(base.body ?? {}, draft.body) : null;
  if (body) patch.body = body as PatchJobBody["body"];
  const note = draft.note.trim();
  if (note !== (job.note ?? "").trim()) patch.note = note ? note : null;
  if (draft.authority !== null && draft.authority !== base.authority)
    patch.authority = draft.authority;
  // A supervised line always names a person, so a move to supervised sends the supervisor with it.
  const supervised = (draft.authority ?? base.authority) === "supervised";
  if (
    supervised &&
    draft.supervisorUserId &&
    (patch.authority !== undefined || draft.supervisorUserId !== base.supervisorUserId)
  ) {
    patch.supervisorUserId = draft.supervisorUserId;
  }
  if (draft.active !== base.active) patch.active = draft.active;
  return Object.keys(patch).length > 0 ? patch : null;
}

/** A body's changed fields; an emptied one goes out as null, because the server merges the body. */
function bodyPatch(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    const next = after[key];
    if (sameJson(before[key], next)) continue;
    out[key] = next === undefined || next === "" ? null : next;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** Read a dotted path off a draft. */
export function readPath(source: unknown, path: string): unknown {
  let cursor: unknown = source;
  for (const key of path.split(".")) {
    if (cursor === null || typeof cursor !== "object") return undefined;
    cursor = (cursor as Record<string, unknown>)[key];
  }
  return cursor;
}

/** Write a dotted path onto a copy of a draft; an empty value deletes the key. */
export function writePath<T extends object>(source: T, path: string, value: unknown): T {
  const clone = structuredClone(source) as Record<string, unknown>;
  const keys = path.split(".");
  let cursor: Record<string, unknown> = clone;
  for (const key of keys.slice(0, -1)) {
    const next = cursor[key];
    if (next === null || typeof next !== "object") cursor[key] = {};
    cursor = cursor[key] as Record<string, unknown>;
  }
  const last = keys[keys.length - 1]!;
  if (value === undefined || value === "" || (Array.isArray(value) && value.length === 0))
    delete cursor[last];
  else cursor[last] = value;
  return clone as T;
}

// ---------------------------------------------------------------------------
// The Team's draft
// ---------------------------------------------------------------------------

/** The "Whose tickets" select's values beside a person's user id. */
export const WHOSE_ANYONE = "";
export const WHOSE_UNASSIGNED = "unassigned";
/** Several people (or people and unassigned) chosen elsewhere: kept as is. */
export const WHOSE_CHOSEN = "__chosen";

export function whoseValue(assignedTo: readonly string[] | null | undefined): string {
  if (!assignedTo || assignedTo.length === 0) return WHOSE_ANYONE;
  if (assignedTo.length === 1) return assignedTo[0]!;
  return WHOSE_CHOSEN;
}

/** Whose tickets names one person: the Team reads that person's assignments. */
export function whoseIsPerson(whose: string): boolean {
  return whose !== WHOSE_ANYONE && whose !== WHOSE_UNASSIGNED && whose !== WHOSE_CHOSEN;
}

export type Tracker = "jira" | "linear";

export interface TeamDraft {
  name: string;
  owns: string;
  repos: string[];
  /** The filter Bailey reads, exactly as written. */
  filter: string;
  tracker: Tracker;
  whose: string;
  /** The delivery target's Slack channel id; empty is nowhere. */
  channel: string;
  /** Who is on the Team, by user id: the roster as read, plus adds, minus removals. */
  people: string[];
  asks: AskRow[];
  audience: SheetAudience;
  /** Reeve's UI preview on plans that change what a person sees. */
  uiPreview: boolean;
  /**
   * The Team ladder step (`desks.autonomy`): it sets every ladder line's rung
   * and "Merge automatically". Null while the Team reads "Custom (override)"
   * and nobody has picked a step in this sheet.
   */
  autonomy: TeamAutonomy | null;
  /** How far one build or one plan may go, and the Team's automatic turns a day. */
  limits: RunLimitsDraft;
  /** The declared system the Team builds inside; null is none. */
  declaredSystemId: string | null;
}

/**
 * Which of the three ways the Team gets tickets these drafts describe. Bailey
 * off (or not on the Team) is "Only what I hand it"; one person's tickets is
 * "Assigned to me"; anything else is "From a filter".
 */
export function readsModeOf(proposeActive: boolean | null, whose: string): ReadsMode {
  if (!proposeActive) return "hand";
  return whoseIsPerson(whose) ? "me" : "filter";
}

/** The Team's first tracker attachment: what Bailey reads today. */
export function readsAttachment(attachments: readonly DeskAttachment[]): DeskAttachment | null {
  return trackerAttachments(attachments)[0] ?? null;
}

// ---------------------------------------------------------------------------
// Save
// ---------------------------------------------------------------------------

export interface SaveInput {
  deskId: string;
  initial: TeamDraft;
  next: TeamDraft;
  attachments: readonly DeskAttachment[];
  members: readonly TeamMember[];
  /** Every line on the Team, as read. */
  jobs: readonly Job[];
  /** The drafts of the lines the sheet holds, by line id. */
  lines: Readonly<Record<string, LineDraft>>;
  checkDrafts: CheckDrafts;
}

export interface SaveOutcome {
  /** Anything that landed with a caveat (a repository not set up yet), said after the save. */
  notes: string[];
  /** Whether anything was written at all. */
  wrote: boolean;
  renamed: boolean;
}

async function sendJson(path: string, method: string, body?: unknown): Promise<unknown> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    ...(body === undefined
      ? {}
      : { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
  });
  const payload = (await res.json().catch(() => null)) as {
    error?: unknown;
    code?: unknown;
  } | null;
  if (!res.ok) {
    throw new TeamWriteError(
      typeof payload?.error === "string" && payload.error.trim()
        ? payload.error.trim()
        : `Request failed (${res.status}).`,
      typeof payload?.code === "string" ? payload.code : "request_failed",
      res.status,
    );
  }
  return payload;
}

/** A pasted link resolved to the one attachment a Team can carry. */
async function resolveFilter(text: string): Promise<DeskAttachmentInput> {
  const answer = (await sendJson("/api/desks/tracker-resolve", "POST", { input: text })) as {
    attachment?: DeskAttachmentInput;
  } | null;
  if (!answer?.attachment)
    throw new TeamWriteError(
      "That link does not name a backlog the Team can read.",
      "not_found",
      404,
    );
  return answer.attachment;
}

/** The attachment a filter, as written, becomes; null when it is empty or unfinished. */
export async function attachmentForFilter(
  text: string,
  tracker: Tracker,
): Promise<DeskAttachmentInput | null> {
  const reading = attachmentForFilterText(text, tracker);
  if (!reading) return null;
  if ("attachment" in reading) return reading.attachment;
  const resolved = await resolveFilter(reading.resolve);
  // A ticket link the tracker finds no epic over stays that ticket, never its project.
  if (reading.unlessEpic && resolved.kind !== "tracker_view") return reading.unlessEpic;
  return resolved;
}

/** Whether the filter as written differs from what the Team reads now. */
export function filterChanged(initial: TeamDraft, next: TeamDraft): boolean {
  return next.filter.trim() !== initial.filter.trim();
}

/**
 * Whether the sheet holds an unsaved edit other than the backlog filter: a
 * Team field, a line, or a check, each only when Save would write it. Leaving the sheet would lose it, so an act
 * that navigates away (Add just KEY) waits until it is saved or discarded.
 */
export function editsBesideFilter(
  initial: TeamDraft,
  next: TeamDraft,
  jobs: readonly Job[],
  lines: Readonly<Record<string, LineDraft>>,
  checkDrafts: Readonly<CheckDrafts>,
): boolean {
  if (!sameJson({ ...initial, filter: "" }, { ...next, filter: "" })) return true;
  if (jobs.some((job) => lines[job.id] != null && linePatch(job, lines[job.id]!) != null))
    return true;
  return Object.values(checkDrafts).some(checkDraftChanged);
}

/**
 * Write what changed, in the order a reader of the queue header would expect
 * it to land: the Team's words, its repositories, what Bailey reads, where it
 * posts, who it asks, whose tickets, who sees it, then each line, then the
 * checks' instructions. The first refusal stops the save and is thrown; what
 * landed before it stays landed and the sheet re-reads it.
 */
export async function saveTeamSettings(input: SaveInput): Promise<SaveOutcome> {
  const { deskId, initial, next, attachments, members, jobs } = input;
  const base = `/api/desks/${encodeURIComponent(deskId)}`;
  const notes: string[] = [];
  let wrote = false;
  let renamed = false;

  const charter: Record<string, unknown> = {};
  if (next.name.trim() && next.name.trim() !== initial.name.trim()) charter.name = next.name.trim();
  if (next.owns.trim() !== initial.owns.trim()) charter.mission = next.owns.trim();
  if (next.uiPreview !== initial.uiPreview) charter.uiPreviewEnabled = next.uiPreview;
  if (next.declaredSystemId !== initial.declaredSystemId)
    charter.declaredSystemId = next.declaredSystemId;
  // The ladder is its own write, after the rest of the charter, so a refused
  // step never takes the Team's other edits with it, and before the lines:
  // the server sets their rungs, so a line's own edit below never carries one.
  const ladder =
    next.autonomy !== null && next.autonomy !== initial.autonomy ? next.autonomy : null;
  const limits = runLimitsCharter(initial.limits, next.limits);
  if (!limits.ok) throw new TeamWriteError(limits.error, "run_limits_invalid", 400);
  Object.assign(charter, limits.charter);
  if (Object.keys(charter).length > 0) {
    await sendJson(`${base}/charter`, "PATCH", charter);
    renamed = "name" in charter;
    wrote = true;
  }
  if (ladder) {
    await sendJson(`${base}/charter`, "PATCH", { autonomy: ladder });
    wrote = true;
  }

  const repos = repoChanges(attachments, next.repos);
  for (const ref of repos.attach) {
    const answer = (await sendJson(`${base}/attachments`, "POST", {
      kind: "repo",
      source: "github",
      ref,
    })) as {
      repoSetup?: { added?: unknown; error?: unknown };
    } | null;
    const setup = answer?.repoSetup;
    if (setup && setup.added === false) {
      notes.push(
        `${ref} is attached but not set up yet${typeof setup.error === "string" && setup.error.trim() ? `: ${setup.error.trim()}` : "."}`,
      );
    }
    wrote = true;
  }
  for (const id of repos.detach) {
    await sendJson(`${base}/attachments/${encodeURIComponent(id)}`, "DELETE");
    wrote = true;
  }

  // The filter, kept as written. A new one is attached before the old one is
  // detached, so a refused attach leaves the Team reading what it read.
  if (filterChanged(initial, next)) {
    const chosen = next.filter.trim() ? await attachmentForFilter(next.filter, next.tracker) : null;
    if (next.filter.trim() && !chosen) {
      throw new TeamWriteError(
        "Finish the filter before saving: Jira cannot read it yet.",
        "filter_unfinished",
        400,
      );
    }
    const writes = readsWrites(attachments, chosen);
    // A narrower backlog withdraws the proposals on tickets it no longer
    // holds; each write says how many it withdrew.
    let retired = 0;
    if (writes.attach) {
      retired += backlogRetiredOf(await attachBacklog(deskId, writes.attach));
      wrote = true;
    }
    for (const id of writes.detach) {
      retired += backlogRetiredOf(
        await sendJson(`${base}/attachments/${encodeURIComponent(id)}`, "DELETE"),
      );
      wrote = true;
    }
    if (retired > 0) notes.push(backlogRetiredNote(retired));
  }

  if (next.channel !== initial.channel) {
    await sendJson(`${base}/delivery-targets`, "PUT", {
      slack: { channelIds: next.channel ? [next.channel] : [] },
    });
    wrote = true;
  }

  const peopleWrote = await withPeopleChanges(
    deskId,
    members,
    peopleChanges(initial.people, next.people),
    async (onTeam) => {
      let roleWrote = false;
      const nextAnswerers: TeamAnswerers = answerersFrom(next.asks);
      if (!sameJson(nextAnswerers, answerersFrom(initial.asks))) {
        await saveAnswerers(deskId, nextAnswerers);
        roleWrote = true;
      }
      if (next.whose !== initial.whose && next.whose !== WHOSE_CHOSEN) {
        if (whoseIsPerson(next.whose) && !onTeam.has(next.whose)) {
          await addTeamMember(deskId, next.whose);
          onTeam.add(next.whose);
        }
        await saveTicketOwners(deskId, next.whose === WHOSE_ANYONE ? [] : [next.whose]);
        roleWrote = true;
      }
      return roleWrote;
    },
  );
  if (peopleWrote) wrote = true;

  if (next.audience !== initial.audience) {
    await sendJson(`${base}/visibility`, "PATCH", { audience: next.audience });
    wrote = true;
  }

  for (const job of jobs) {
    const draft = input.lines[job.id];
    if (!draft) continue;
    const rung =
      ladder && isTeamLadderLineKind(job.kind) ? ladderSettingsFor(ladder).authority : null;
    const patch = rung
      ? linePatch({ ...job, authority: rung }, { ...draft, authority: rung })
      : linePatch(job, draft);
    if (!patch) continue;
    await patchJob(deskId, job.id, patch);
    wrote = true;
  }

  const refused = await saveCheckDrafts(deskId, input.checkDrafts);
  if (refused) throw new TeamWriteError(refused, "checks_refused", 400);
  if (Object.keys(input.checkDrafts).length > 0) wrote = true;

  return { notes, wrote, renamed };
}

/** `backlogRetired` off an attachment write's answer; 0 when it withdrew nothing. */
function backlogRetiredOf(answer: unknown): number {
  const n = (answer as { backlogRetired?: unknown } | null)?.backlogRetired;
  return typeof n === "number" && Number.isInteger(n) && n > 0 ? n : 0;
}

/** What the sheet says once a narrower backlog withdrew proposals. */
export function backlogRetiredNote(count: number): string {
  return count === 1
    ? "1 proposal is no longer in this Team's backlog, so it was withdrawn."
    : `${count} proposals are no longer in this Team's backlog, so they were withdrawn.`;
}

/**
 * The People writes around the writes that name people (`roles`: who is
 * asked, whose tickets). Adds go first, because an answerer or a ticket
 * owner has to be on the Team already; removals go last, once those have let
 * go of them (the server refuses the owner and anyone still supervising a
 * line, by name). Returns whether anything was written.
 */
async function withPeopleChanges(
  deskId: string,
  members: readonly TeamMember[],
  people: { add: string[]; remove: string[] },
  roles: (onTeam: Set<string>) => Promise<boolean>,
): Promise<boolean> {
  const onTeam = new Set(members.map((member) => member.userId));
  let wrote = false;
  for (const userId of people.add) {
    if (onTeam.has(userId)) continue;
    await addTeamMember(deskId, userId);
    onTeam.add(userId);
    wrote = true;
  }
  if (await roles(onTeam)) wrote = true;
  for (const userId of people.remove) {
    if (!onTeam.has(userId)) continue;
    await removeTeamMember(deskId, userId);
    onTeam.delete(userId);
    wrote = true;
  }
  return wrote;
}

/** Who the People field adds and removes, in the order they were picked. */
export function peopleChanges(
  initial: readonly string[],
  next: readonly string[],
): { add: string[]; remove: string[] } {
  return {
    add: next.filter((userId) => !initial.includes(userId)),
    remove: initial.filter((userId) => !next.includes(userId)),
  };
}

/** The filter text the Team reads now, for the drafts' starting point. */
export function initialFilterText(attachments: readonly DeskAttachment[]): string {
  const att = readsAttachment(attachments);
  return att ? (filterTextOf(att) ?? "") : "";
}
